<script>
  import { link } from "svelte-routing";
  import AuthNavbar from "../components/Navbars/AuthNavbar.svelte";
  import FooterSmall from "../components/Footers/FooterSmall.svelte";
  import WaitlistForm from "../components/WaitlistForm.svelte";
  import Logo from "../components/Logo.svelte";
  import { config } from "../stores/config.js";

  const steps = [
    {
      n: "01",
      title: "Create",
      body: "Open an account and design your first pie — a portfolio drawn as portions of a whole.",
    },
    {
      n: "02",
      title: "Allocate",
      body: "Assign each stock a slice. Invest any amount; fractional shares mean every dollar lands exactly where you intended.",
    },
    {
      n: "03",
      title: "Execute",
      body: "Recurring deposits buy into your targets automatically, and rebalancing keeps every slice the size you chose.",
    },
  ];

  const features = [
    {
      icon: "fas fa-chart-pie",
      tint: "bg-emerald-50 text-moira",
      title: "Pie-based portfolios",
      body: "Your whole portfolio in one picture. Each slice is a position with a target weight you control.",
    },
    {
      icon: "fas fa-rotate",
      tint: "bg-indigo-50 text-accent",
      title: "Automated investing",
      body: "Schedule deposits and let Moira distribute funds across your pies according to plan.",
    },
    {
      icon: "fas fa-scale-balanced",
      tint: "bg-amber-50 text-warning",
      title: "Dynamic rebalancing",
      body: "Drift happens. Moira nudges your holdings back toward their target allocations.",
    },
    {
      icon: "fas fa-shield-halved",
      tint: "bg-blue-50 text-info",
      title: "SIPC protected",
      body: "Accounts protected up to $500,000, including $250,000 for cash claims.",
    },
  ];

  const comparison = [
    { feature: "Monthly fees", moira: { text: "$0" }, m1: { text: "$3/mo" }, fidelity: { text: "$5/mo" } },
    { feature: "Pie-based portfolios", moira: { check: true }, m1: { check: true }, fidelity: { check: true } },
    { feature: "Fractional shares", moira: { check: true }, m1: { check: true }, fidelity: { check: true } },
    { feature: "Auto rebalancing", moira: { check: true }, m1: { check: true }, fidelity: { check: false } },
    { feature: "Commission-free trading", moira: { check: true }, m1: { check: true }, fidelity: { check: true } },
    { feature: "Automated investing", moira: { check: true }, m1: { check: true }, fidelity: { check: true } },
    { feature: "SIPC protection", moira: { check: true }, m1: { check: true }, fidelity: { check: true } },
    { feature: "Minimum deposit", moira: { text: "None" }, m1: { text: "$100" }, fidelity: { text: "None" } },
  ];
</script>

<div class="bg-canvas">
  <AuthNavbar />

  <main>
    <!-- ── Hero ──────────────────────────────────────────────────────────── -->
    <section class="border-b border-line bg-surface">
      <div class="max-w-6xl mx-auto px-4 md:px-8 pt-32 pb-16 md:pt-40 md:pb-24">
        <div class="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p class="text-xs font-semibold uppercase tracking-wider text-moira-dark mb-4">
              Pie-based investing
            </p>
            <h1 class="font-display text-5xl md:text-6xl font-medium tracking-tight text-ink leading-[1.05]">
              Investing,<br />measured.
            </h1>
            <p class="mt-6 text-lg text-ink-secondary leading-relaxed max-w-md">
              Design your portfolio as portions of a whole. Moira automates the
              deposits, the buys, and the rebalancing — so every dollar lands
              where you intended.
            </p>

            <div class="mt-8 flex flex-wrap items-center gap-3">
              {#if $config.authEnabled}
                <a use:link href="/auth/login" class="btn btn-primary">
                  Get started
                </a>
              {:else}
                <a href="#waitlist" class="btn btn-primary">
                  Join the waitlist
                </a>
              {/if}
              <a href="#how-it-works" class="btn btn-ghost">
                See how it works
              </a>
            </div>

            <p class="mt-8 text-sm text-ink-tertiary">
              Commission-free&ensp;·&ensp;No account minimums&ensp;·&ensp;SIPC protected
            </p>
          </div>

          <div class="flex justify-center lg:justify-end">
            <img
              src="/assets/img/device_with_app_nobg.png"
              alt="The Moira Finance app showing a pie portfolio"
              class="w-64 sm:w-72 lg:w-80 xl:w-96 h-auto drop-shadow-xl"
              loading="eager"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- ── Brand note ────────────────────────────────────────────────────── -->
    <section class="max-w-3xl mx-auto px-4 md:px-8 py-16 md:py-20 text-center">
      <p class="font-display text-xl md:text-2xl text-ink-secondary italic leading-relaxed">
        Named for the Moirai — the three Fates who spun, measured, and cut the
        thread of life. We built Moira around the second of them: Lachesis,
        who decided how much each portion would be.
      </p>
    </section>

    <!-- ── Three steps ───────────────────────────────────────────────────── -->
    <section id="how-it-works" class="border-t border-line bg-surface">
      <div class="max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-24">
        <h2 class="font-display text-3xl md:text-4xl font-medium tracking-tight text-ink">
          A portfolio in three acts
        </h2>
        <div class="grid md:grid-cols-3 gap-8 md:gap-10 mt-12">
          {#each steps as step}
            <div class="border-t-2 border-moira pt-6">
              <p class="text-sm font-semibold text-moira tnum">{step.n}</p>
              <h3 class="text-lg font-semibold text-ink mt-2">{step.title}</h3>
              <p class="text-sm text-ink-secondary leading-relaxed mt-2">{step.body}</p>
            </div>
          {/each}
        </div>
      </div>
    </section>

    <!-- ── Features ──────────────────────────────────────────────────────── -->
    <section class="max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-24">
      <div class="max-w-2xl">
        <h2 class="font-display text-3xl md:text-4xl font-medium tracking-tight text-ink">
          Built for deliberate investors
        </h2>
        <p class="text-base text-ink-secondary mt-4 leading-relaxed">
          Moira combines the discipline of robo-advising with full control over
          what you own. You decide what to invest in; we handle the execution.
        </p>
      </div>
      <div class="grid sm:grid-cols-2 gap-5 mt-12">
        {#each features as feature}
          <div class="card card-pad">
            <div class="w-11 h-11 rounded-xl inline-flex items-center justify-center {feature.tint}">
              <i class={feature.icon}></i>
            </div>
            <h3 class="text-base font-semibold text-ink mt-4">{feature.title}</h3>
            <p class="text-sm text-ink-secondary leading-relaxed mt-1.5">{feature.body}</p>
          </div>
        {/each}
      </div>
    </section>

    <!-- ── Comparison ────────────────────────────────────────────────────── -->
    <section class="border-t border-line bg-surface">
      <div class="max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-24">
        <div class="max-w-2xl">
          <h2 class="font-display text-3xl md:text-4xl font-medium tracking-tight text-ink">
            How Moira compares
          </h2>
          <p class="text-base text-ink-secondary mt-4">
            The pie model you know — without the subscription.
          </p>
        </div>

        <div class="card overflow-hidden mt-12">
          <div class="overflow-x-auto">
            <table class="min-w-full">
              <thead>
                <tr>
                  <th class="th w-1/3"></th>
                  <th class="th !text-ink text-center bg-emerald-50/60">Moira Finance</th>
                  <th class="th text-center">M1 Finance</th>
                  <th class="th text-center">Fidelity Basket Slices</th>
                </tr>
              </thead>
              <tbody>
                {#each comparison as row}
                  <tr>
                    <td class="td font-medium">{row.feature}</td>
                    <td class="td text-center bg-emerald-50/40 font-semibold text-ink tnum">
                      {#if row.moira.check !== undefined}
                        {#if row.moira.check}<i class="fas fa-check text-moira"></i>{:else}<i class="fas fa-minus text-ink-tertiary"></i>{/if}
                      {:else}
                        {row.moira.text}
                      {/if}
                    </td>
                    <td class="td text-center text-ink-secondary tnum">
                      {#if row.m1.check !== undefined}
                        {#if row.m1.check}<i class="fas fa-check text-ink-secondary"></i>{:else}<i class="fas fa-minus text-ink-tertiary"></i>{/if}
                      {:else}
                        {row.m1.text}
                      {/if}
                    </td>
                    <td class="td text-center text-ink-secondary tnum">
                      {#if row.fidelity.check !== undefined}
                        {#if row.fidelity.check}<i class="fas fa-check text-ink-secondary"></i>{:else}<i class="fas fa-minus text-ink-tertiary"></i>{/if}
                      {:else}
                        {row.fidelity.text}
                      {/if}
                    </td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        </div>

        <p class="text-sm text-ink-tertiary mt-4">
          No platform fee means saving $36 a year compared with M1 Finance, and
          $60 a year compared with Fidelity Basket Slices. Pricing as published
          by each platform, June 2026.
        </p>
      </div>
    </section>

    <!-- ── Closing CTA / waitlist ────────────────────────────────────────── -->
    <section id="waitlist" class="bg-slate-900">
      <div class="max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-24">
        {#if $config.authEnabled}
          <div class="max-w-2xl mx-auto text-center">
            <Logo size={36} dark wordmark={false} />
            <h2 class="font-display text-3xl md:text-4xl font-medium tracking-tight text-white mt-6">
              Decide your portions.
            </h2>
            <p class="text-base text-slate-400 mt-4">
              Open an account in minutes. No fees, no minimums.
            </p>
            <a use:link href="/auth/login" class="btn btn-primary mt-8">
              Get started
            </a>
          </div>
        {:else}
          <div class="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Logo size={36} dark wordmark={false} />
              <h2 class="font-display text-3xl md:text-4xl font-medium tracking-tight text-white mt-6">
                Be first in line.
              </h2>
              <p class="text-base text-slate-400 mt-4 leading-relaxed max-w-md">
                We're putting the finishing touches on Moira Finance. Join the
                waitlist and we'll send one email — the day we launch.
              </p>
            </div>
            <div class="card card-pad lg:justify-self-end w-full max-w-md">
              <WaitlistForm compact={true} />
            </div>
          </div>
        {/if}
      </div>
      <FooterSmall dark={true} />
    </section>
  </main>
</div>
